"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  TURKISH_CASE_STRINGS,
  TurkishCaseToggle,
  useTurkishCase,
} from "@/components/tools/turkish-case-toggle";
import { writeToClipboard } from "@/lib/clipboard";
import {
  capitalizeFirst,
  foldLower,
  foldUpper,
  hasCase,
} from "@/lib/turkish-case";

/** Words and the separators between them, kept so the output keeps the spacing. */
const RE_WORD_SEPARATORS = /(\s+|—)/;
const RE_SPACE_ONLY = /^\s*$/;
/*
 * A word that ends a title's first part: the next word opens a subtitle. Periods are
 * left out on purpose — "U.S." and "vs." end in one far more often than a title's own
 * sentence does.
 */
const RE_OPENS_SUBTITLE = /[:?!—]["'”’)\]]*$/u;
const RE_EDGE_PUNCTUATION = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;
const RE_LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
/** An acronym as typed: two or more capitals, optionally dotted ("NASA", "U.S."). */
const RE_ACRONYM = /^(?:\p{Lu}\.?){2,}$/u;
const RE_LOWERCASE_LETTER = /\p{Ll}/u;

/*
 * Sentence starts by Unicode property, not by `\w`.
 *
 * `\w` is `[A-Za-z0-9_]` in JavaScript even under the `u` flag, so the `Sentence case`
 * button did nothing at all to a sentence that did not open on an ASCII letter:
 * "десять способов. это второе." and "đây là câu. ứng dụng tốt." came back entirely
 * lower-case, and German "über allen gipfeln ist ruh. ölberg ist schön." was untouched
 * at both sentence starts. Only the sentence's first character is matched and folded up,
 * so the class needs no `\p{M}` — nothing here has to hold a whole word together.
 */
const RE_SENTENCE_START = /(^\s*[\p{L}\p{N}_]|[.!?]\s+[\p{L}\p{N}_])/gu;

/*
 * APA 7 title case (Publication Manual §6.17): major words — nouns, verbs including
 * linking verbs, adjectives, adverbs, pronouns — and every word of four letters or more
 * are capitalized. Only these minor words stay lowercase, and only when they do not open
 * the title or a subtitle: articles, and conjunctions and prepositions of three letters
 * or fewer, as APA lists them ("up" and "off" only as prepositions; see PARTICLE_WORDS).
 * "is" and "it" are a linking verb and a pronoun, so they
 * are major words.
 *
 * APA has no last-word rule. Capitalizing the last word whatever it is belongs to
 * Chicago and MLA headline style, so a title ending in a short preposition keeps it
 * lowercase here.
 */
const MINOR_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "as",
  "but",
  "for",
  "if",
  "nor",
  "or",
  "so",
  "yet",
  "at",
  "by",
  "in",
  "of",
  "on",
  "per",
  "to",
  "via",
]);

/*
 * "up" and "off" are on APA's list of short prepositions, but they are as often adverbs
 * ("Grow Up", "Cut Off"), and APA capitalizes adverbs. They stay lower case only as a
 * preposition, which is to say when an article follows ("Off the Grid", "Up the River").
 */
const PARTICLE_WORDS = new Set(["off", "up"]);
const ARTICLES = new Set(["a", "an", "the"]);

/** Capitalize the first letter, past any opening quotation mark or bracket. */
function capitalizeWord(word: string, turkish: boolean): string {
  const start = word.search(RE_LETTER_OR_DIGIT);
  if (start < 0) {
    return word;
  }
  return word.slice(0, start) + capitalizeFirst(word.slice(start), turkish);
}

/**
 * `MINOR_WORDS` is an English list, so it is matched against the locale-less fold:
 * Turkish folding turns "IN" into "ın", which is in no English table.
 */
function isMinorWord(word: string, nextWord: string | undefined): boolean {
  const bare = word.replace(RE_EDGE_PUNCTUATION, "").toLowerCase();
  if (PARTICLE_WORDS.has(bare)) {
    return (
      nextWord !== undefined &&
      ARTICLES.has(nextWord.replace(RE_EDGE_PUNCTUATION, "").toLowerCase())
    );
  }
  return MINOR_WORDS.has(bare);
}

/** Only in a line that is not all capitals: there, a capitalized word was meant. */
function isTypedAcronym(word: string): boolean {
  return RE_ACRONYM.test(word.replace(RE_EDGE_PUNCTUATION, ""));
}

/**
 * One word. A hyphenated compound counts as a major word, so its first part is always
 * capitalized, and APA capitalizes the later parts too ("Self-Report") unless one is
 * itself a minor word ("Face-to-Face").
 */
function titleCaseWord(
  word: string,
  opensTitle: boolean,
  turkish: boolean,
  context: { keepAcronyms: boolean; nextWord?: string }
): string {
  const parts = word.split("-");
  if (parts.length > 1) {
    return parts
      .map((part, index) =>
        titleCaseWord(part, index === 0, turkish, {
          keepAcronyms: context.keepAcronyms,
        })
      )
      .join("-");
  }
  if (context.keepAcronyms && isTypedAcronym(word)) {
    return word;
  }
  const lower = foldLower(word, turkish);
  if (!opensTitle && isMinorWord(word, context.nextWord)) {
    return lower;
  }
  return capitalizeWord(lower, turkish);
}

/** The word after segment `index`, skipping spaces; undefined at a dash or the end. */
function wordAfter(segments: string[], index: number): string | undefined {
  for (let i = index + 1; i < segments.length; i++) {
    if (!RE_SPACE_ONLY.test(segments[i])) {
      return segments[i] === "—" ? undefined : segments[i];
    }
  }
  return;
}

function titleCaseLine(line: string, turkish: boolean): string {
  let opensTitle = true;
  // A pasted ALL CAPS line cannot say which words are acronyms, so it is folded whole.
  const keepAcronyms = RE_LOWERCASE_LETTER.test(line);
  const segments = line.split(RE_WORD_SEPARATORS);
  return segments
    .map((segment, index) => {
      if (RE_SPACE_ONLY.test(segment)) {
        return segment;
      }
      if (segment === "—") {
        opensTitle = true;
        return segment;
      }
      const result = titleCaseWord(segment, opensTitle, turkish, {
        keepAcronyms,
        nextWord: wordAfter(segments, index),
      });
      opensTitle = RE_OPENS_SUBTITLE.test(segment);
      return result;
    })
    .join("");
}

/** Each line is its own title, so a pasted list of titles converts line by line. */
export function toTitleCaseAPA(text: string, turkish: boolean): string {
  return text
    .split("\n")
    .map((line) => titleCaseLine(line, turkish))
    .join("\n");
}

function toSentenceCase(text: string, turkish: boolean): string {
  return foldLower(text, turkish).replace(RE_SENTENCE_START, (match) =>
    foldUpper(match, turkish)
  );
}

/*
 * `hasCase`, not `[a-zA-Z]` and not `\p{L}`.
 *
 * The ASCII test skipped accented letters without advancing the alternation index, so
 * "Grüße aus Köln" alternated as "gRüße AuS köLn" and "Десять способов писать" came back
 * completely untouched. `\p{L}` would fix those two and break the unicameral scripts:
 * folding a Hangul, Arabic, Devanagari or Han character returns it unchanged, so the
 * index would advance on characters that never alternate. See `hasCase` in
 * `lib/turkish-case.ts`.
 */
function toAlternatingCase(text: string, turkish: boolean): string {
  let i = 0;
  return text
    .split("")
    .map((char) => {
      if (hasCase(char)) {
        const result =
          i % 2 === 0 ? foldLower(char, turkish) : foldUpper(char, turkish);
        i++;
        return result;
      }
      return char;
    })
    .join("");
}

type CaseMode = "title" | "upper" | "lower" | "sentence" | "alternating";

/** English strings. A translated content file overrides these through `ui`. */
export const TITLE_CASE_CONVERTER_STRINGS = {
  ...TURKISH_CASE_STRINGS,
  inputText: "Input Text",
  placeholder: "Type or paste your text here...",
  modeLabels: {
    title: "Title Case (APA)",
    upper: "UPPERCASE",
    lower: "lowercase",
    sentence: "Sentence case",
    alternating: "aLtErNaTiNg",
  },
  output: "Output",
  copied: "Copied!",
  copyToClipboard: "Copy to Clipboard",
} as const;

export default function TitleCaseConverterWidget() {
  const strings = useToolUi(TITLE_CASE_CONVERTER_STRINGS);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [activeMode, setActiveMode] = useState<CaseMode | null>(null);
  const [copied, setCopied] = useState(false);
  const [turkish, setTurkish] = useTurkishCase();

  function convert(mode: CaseMode) {
    setActiveMode(mode);
    let result = "";
    switch (mode) {
      case "title":
        result = toTitleCaseAPA(input, turkish);
        break;
      case "upper":
        result = foldUpper(input, turkish);
        break;
      case "lower":
        result = foldLower(input, turkish);
        break;
      case "sentence":
        result = toSentenceCase(input, turkish);
        break;
      case "alternating":
        result = toAlternatingCase(input, turkish);
        break;
      default:
        break;
    }
    setOutput(result);
    setCopied(false);
  }

  async function handleCopy() {
    if (output) {
      if (!(await writeToClipboard(output))) {
        return;
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  const modes: CaseMode[] = [
    "title",
    "upper",
    "lower",
    "sentence",
    "alternating",
  ];

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="tc-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="tc-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
      </div>
      <TurkishCaseToggle
        checked={turkish}
        onChange={setTurkish}
        strings={strings}
      />

      {/* Mode buttons */}
      <div className="flex flex-wrap gap-2">
        {modes.map((mode) => (
          <button
            className={`rounded-lg border px-4 py-2 text-sm transition-colors ${
              activeMode === mode
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-foreground hover:border-foreground/20 hover:bg-accent"
            }`}
            key={mode}
            onClick={() => convert(mode)}
            type="button"
          >
            {strings.modeLabels[mode]}
          </button>
        ))}
      </div>

      {/* Output */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="tc-output"
          >
            {strings.output}
          </label>
          {output && (
            <button
              className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyToClipboard}
            </button>
          )}
        </div>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-muted/30 p-4 text-foreground text-sm leading-relaxed"
          id="tc-output"
          readOnly
          value={output}
        />
      </div>
    </>
  );
}
